import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import {
  FIRST_LOGIN_PARAMETER_NAME,
  FirstLoginModalComponent,
} from "../../../shared";
import { DashboardFiltersComponent } from "./components/dashboard-filters/dashboard-filters.component";
import { LocationPerformanceListComponent } from "./components/location-performance-list/location-performance-list.component";
import { AsyncPipe } from "@angular/common";
import {
  DataLayerModel,
  GtmSupportAbstractComponent,
  StorageService,
  SurveyEnum,
  SurveyService,
} from "@merchant-webapp/components";
import { UserStore } from "../../../shared/stores";
import { TranslocoPipe } from "@jsverse/transloco";
import { NgSelectModule } from "@ng-select/ng-select";
import { DirectionIndicatorComponent } from "./components/direction-indicator/direction-indicator.component";
import { MatIconModule } from "@angular/material/icon";
import { DonutChartComponent } from "../../../shared/components/donut-chart/donut-chart.component";
import { FormsModule } from "@angular/forms";
import { EdTooltipComponent } from "@edenred/components/tooltip";
import { StackedBarChartComponent } from "../../../shared/components/stacked-bar-chart/stacked-bar-chart.component";
import { TimePeriodChangeDataLayerModel } from "../../../shared/models/gtm";
import { TIME_ON_PAGE_SURVEY_TRIGGER } from "@merchant-webapp/components";
import { environment } from "../../../../environments";
import { DashboardTypeEnum } from "./enums/dashboard-type.enum";
import { DashboardTimeSpentDataLayerModel } from "../../../shared/models/gtm/dashboard-time-spent.data-layer.model";
import { EdButtonComponent } from "@edenred/components/button";
import { DashboardStore, timePeriod } from "./services/dashboard.store";
import { MixpanelService } from "../../../shared/services/mixpanel.service";
import { CONFIRM_SWITCH_BANNER_CLOSED } from "../../../shared/constants/session-storage-keys.constants";
import { DigitalInvoiceConsentComponent } from "../../../shared/components/digital-invoice-consent/digital-invoice-consent.component";
import { FuelPerformanceItemsComponent } from "./components/performance-items/fuel-performance-items/fuel-performance-items.component";
import { PlusPerformanceItemsComponent } from "./components/performance-items/plus-performance-items/plus-performance-items.component";
import { StackedBarChartLabels } from "../../../shared/components/stacked-bar-chart/stacked-bar-chart.model";
import { FeatureFlagStore } from "../../../shared/services/feature-flag.service";

const i18Cmp = "dashboard";
const i18CmpTimePeriodDropdown = `${i18Cmp}.time_period_dropdown`;
const i18CmpLocationPerformace = `${i18Cmp}.location_performance`;
export const i18CmpProductItems = `${i18CmpLocationPerformace}.product_items`;
export const i18CmpTimePeriodDropdownOptions = `${i18CmpTimePeriodDropdown}.options`;
export const i18CmpTotalPerformance = `${i18Cmp}.total_performance`;
export const i18CmpDirectionCompare = `${i18CmpTotalPerformance}.direction_compare`;
export const i18CmpTotalPerformanceItems = `${i18CmpTotalPerformance}.performance_items`;
const i18CmpDirectionLabel = `${i18CmpTotalPerformance}.direction`;
const i18CmpYourTotalVolumesChart = `${i18Cmp}.your_total_volumes`;
const i18CmpInvoicedTransactionChart = `${i18Cmp}.invoiced_transaction`;
const i18CmpPlusCustomerLocationChart = `${i18Cmp}.plus_customer_location`;
export const i18CmpFuelVolumeOverviewChart = `${i18Cmp}.fuel_volume_overview`;
export const i18CmpFuelVolumeOverviewChartItems = `${i18CmpFuelVolumeOverviewChart}.items`;
export const i18CmpInvoicedTransactionItems = `${i18CmpInvoicedTransactionChart}.items`;
export const i18CmpPlusCustomerLocationItems = `${i18CmpPlusCustomerLocationChart}.items`;
export const DEFAULT_TIME_PERIOD_ID = "LAST_MONTH";

@Component({
  selector: "er-merchant-dashboard",
  templateUrl: "./dashboard.component.html",
  styleUrls: ["./dashboard.component.scss"],
  standalone: true,
  imports: [
    MatDialogModule,
    DashboardFiltersComponent,
    LocationPerformanceListComponent,
    AsyncPipe,
    MatDialogModule,
    EdTooltipComponent,
    NgSelectModule,
    FormsModule,
    DirectionIndicatorComponent,
    TranslocoPipe,
    MatIconModule,
    DonutChartComponent,
    StackedBarChartComponent,
    EdButtonComponent,
    DigitalInvoiceConsentComponent,
    FuelPerformanceItemsComponent,
    PlusPerformanceItemsComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardComponent
  extends GtmSupportAbstractComponent
  implements OnInit, OnDestroy
{
  public dispColumns: string[] = [
    "dieselVolume",
    "petrolVolume",
    "adblueVolume",
  ];
  public showDigitalInvoiceConsentBanner = false;
  public confirmSwitchBannerClosed = false;
  public totalVolumesStackBarChartLabels!: StackedBarChartLabels;

  public readonly i18Cmp = i18Cmp;
  public readonly i18CmpTimePeriodDropdown = i18CmpTimePeriodDropdown;
  public readonly i18CmpTimePeriodDropdownOptions =
    i18CmpTimePeriodDropdownOptions;
  public readonly i18CmpTotalPerformance = i18CmpTotalPerformance;
  public readonly i18CmpTotalPerformanceItems = i18CmpTotalPerformanceItems;
  public readonly i18CmpYourTotalVolumesChart = i18CmpYourTotalVolumesChart;
  public readonly i18CmpInvoicedTransactionChart =
    i18CmpInvoicedTransactionChart;
  public readonly i18CmpFuelVolumeOverviewChart = i18CmpFuelVolumeOverviewChart;
  public readonly i18CmpProductItems = i18CmpProductItems;
  public readonly i18CmpDirectionLabel = i18CmpDirectionLabel;
  public readonly i18CmpPlusCustomerLocationChart =
    i18CmpPlusCustomerLocationChart;

  protected readonly ffs = inject(FeatureFlagStore);
  protected readonly userStore: any = inject(UserStore);
  protected readonly dashboardStore = inject(DashboardStore);
  protected readonly timePeriod = timePeriod;

  private surveyTimer: any;
  private dashboardTimeStart: Map<DashboardTypeEnum, number> = new Map<
    DashboardTypeEnum,
    number
  >();
  private dashboardTotalTimeSpent: Map<DashboardTypeEnum, number> = new Map<
    DashboardTypeEnum,
    number
  >();

  private readonly surveyService: SurveyService = inject(SurveyService);
  private readonly mixpanelService: MixpanelService = inject(MixpanelService);

  constructor(
    private readonly dialog: MatDialog,
    private readonly storageService: StorageService,
  ) {
    super(environment.gtm);

    Object.values(DashboardTypeEnum).forEach((value: DashboardTypeEnum) => {
      this.dashboardTimeStart.set(value, 0);
      this.dashboardTotalTimeSpent.set(value, 0);
    });
  }

  ngOnInit(): void {
    this.dashboardStore.resetState();
    this.displayMarketingWindowsIfFirstLogin();
    this.startSurveyTriggerTimer();
    this.startPageTimer();
    this.initSwitchBannerClosed();
    this.initStackedBarChartLabels();
  }

  public ngOnDestroy(): void {
    this.clearInterval();
    this.sendPageTimeSpentEvent();
    this.sendDashboardTimeSpentEvent();
  }

  public initStackedBarChartLabels() {
    const stackedBarChartType = this.userStore.isPlusStation()
      ? this.i18CmpInvoicedTransactionChart
      : this.i18CmpYourTotalVolumesChart;
    this.totalVolumesStackBarChartLabels = {
      title: `${stackedBarChartType}.title`,
      multiselect: `${stackedBarChartType}.multiselect`,
      legend: `${stackedBarChartType}.legend`,
    };
  }

  public onCloseBanner(): void {
    sessionStorage.setItem(CONFIRM_SWITCH_BANNER_CLOSED, JSON.stringify(true));
    this.initSwitchBannerClosed();
  }

  public onMouseEnter(type: any): void {
    const dashboardType: DashboardTypeEnum = type;
    this.dashboardTimeStart.set(dashboardType, performance.now());
  }

  public onMouseLeave(type: any): void {
    const dashboardType: DashboardTypeEnum = type;
    const timeStart: number | undefined =
      this.dashboardTimeStart.get(dashboardType);
    const timeEnd: number = performance.now();
    if (timeStart) {
      const timeSpent: number = timeEnd - timeStart;
      const timeSpentInSeconds: number = Math.floor(timeSpent / 1000);
      let totalTimeInSeconds: number | undefined =
        this.dashboardTotalTimeSpent.get(dashboardType);
      if (totalTimeInSeconds) {
        totalTimeInSeconds += timeSpentInSeconds;
        this.dashboardTotalTimeSpent.set(dashboardType, totalTimeInSeconds);
      } else {
        this.dashboardTotalTimeSpent.set(dashboardType, timeSpentInSeconds);
      }
    }
  }

  public onTimePeriodChange(event: any) {
    this.dashboardStore.setTimePeriod(event.id);
    this.gtmService.pushEventToDataLayer(
      new TimePeriodChangeDataLayerModel(event.id),
    );
    this.mixpanelService.track(
      TimePeriodChangeDataLayerModel.EVENT_NAME,
      event.id,
    );
  }

  public toggleColumnVisibility(dispColumns: string[]) {
    this.dashboardStore.setDisplayedColumns(dispColumns);
  }

  public filterDataByName(station: string) {
    this.dashboardStore.partialUpdateStationPerformanceFilter({ station });
  }

  private initSwitchBannerClosed() {
    this.confirmSwitchBannerClosed = JSON.parse(
      sessionStorage.getItem(CONFIRM_SWITCH_BANNER_CLOSED) || "false",
    );
  }

  private displayMarketingWindowsIfFirstLogin(): void {
    const isFirstLogin = this.storageService.getData(
      FIRST_LOGIN_PARAMETER_NAME,
    );
    if (isFirstLogin === "true") {
      this.storageService.storeData(FIRST_LOGIN_PARAMETER_NAME, false);
      this.dialog.open(FirstLoginModalComponent, {
        width: "500px",
        enterAnimationDuration: "1000ms",
        exitAnimationDuration: "1000ms",
        disableClose: true,
      });
    }
  }

  private startSurveyTriggerTimer(): void {
    this.surveyTimer = setInterval((): void => {
      this.surveyService.updateSurveyVariable(SurveyEnum.DASHBOARD, true);
      this.clearInterval();
    }, TIME_ON_PAGE_SURVEY_TRIGGER);
  }

  private clearInterval(): void {
    clearInterval(this.surveyTimer);
  }

  private sendDashboardTimeSpentEvent(): void {
    const dataLayerModels: DataLayerModel[] = Array.from(
      this.dashboardTotalTimeSpent.entries(),
    ).map(([key, value]: [DashboardTypeEnum, number]) => {
      return new DashboardTimeSpentDataLayerModel(key, value);
    });
    this.gtmService.pushMultipleEventsToDataLayer(dataLayerModels);
  }
}
